import angular from "angular";
import { ApiImportStatus, Imports, DataMapper, CompanyImportRow,CompanyImport, ApiValidationErrorType, Translations, EInvoiceAddress } from "@salaxy/core";

import { DataImportControllerBase } from "./DataImportControllerBase";

import { EditDialogKnownActions, JsonSchemaService, UiHelpers } from "../../../services";


/**
 * Controller for company imports.
 */
export class CompanyImportController extends DataImportControllerBase<CompanyImportRow, CompanyImport, never> {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["Imports", "JsonSchemaService", "$timeout", "UiHelpers"];

  private dataEnricher = {
    enrich: () => this.enrichEInvoiceAddresses(),
    buttonLabel: Translations.get("SALAXY.NG1.CompanyImport.enrichEInvoiceAddressesButton")
  };

  /**
   * Creates a new WorkerImportController
   * @ignore
   */
  constructor(
    private importsApi: Imports,
    jsonSchemaService: JsonSchemaService,
    $timeout: angular.ITimeoutService,
    private uiHelpers: UiHelpers,
  ) {
    super(jsonSchemaService, $timeout);
  }

  /**
   * Implement IController
   */
  public async $onInit(){
    super.$onInit();
  }


  /** Enrichs data */
  public getDataEnrichers(): Array<{ enrich: () => void, buttonLabel: string }> {
    return [this.dataEnricher];
  }


  /** Import data */
  public async import() {

    if (this.isImporting) {
      return;
    }

    await this.validate();
    if (!this.targetData) {
      return;
    }

    this.isImporting = true;
    this.isCanceled = false;

    for(const sourceRow of this.sourceData) {
      sourceRow.validation = null;
      sourceRow.importStatus = ApiImportStatus.Ignored;
    }
    this.table.setData(this.sourceData);
    await this.$timeout();

    const getTargetKey = (x: CompanyImport) => `${x.item.officialId}`.toLowerCase();
    const getSourceKey = (x: CompanyImportRow) => `${x.officialId}`.toLowerCase();

    this.importTotal = this.targetData.length;
    this.importCurrent = 0;

    const chunkSize = 10;
    const chunks: CompanyImport[][] = [];
    for (let i = 0; i < this.targetData.length; i += chunkSize) {
        chunks.push(this.targetData.slice(i, i + chunkSize));
    }

    let index = 0;
    for(const chunk of chunks) {
      try {
        if (this.isCanceled) {
          break;
        }
        chunk.splice(0, chunk.length, ... await this.importsApi.importCompanies(chunk));
      } catch (error) {
        for( const importObj of chunk) {
          importObj.result = importObj.result ?? {};
          importObj.result.status = ApiImportStatus.Error,
          importObj.validation = {
            isValid: false,
            errors: [{
              key: null,
              type: ApiValidationErrorType.General,
              code: "Server error",
              msg: error?.data?.messageHtml || JSON.stringify(error?.data, Object.getOwnPropertyNames(error?.data)),
            }],
          };
        }
      }
      for(const result of chunk) {
        const sourceRows = this.sourceData.filter((x) => getSourceKey(x) == getTargetKey(result));
        for(const sourceRow of sourceRows) {
          sourceRow.validation = {
            isValid: result.validation?.isValid,
            errors: result.validation?.errors
          };
          sourceRow.importStatus = result.result?.status;
        }
        index += sourceRows.length;
        this.importCurrent = index;

        this.table.setData(this.sourceData);
        await this.$timeout();
      }
    }
    this.isImporting = false;
    await this.$timeout();
  }

  /** Help */
  public async help() {
    const helpData = [
      [Translations.get("SALAXY.NG1.CompanyImport.help.title")],
      [""],
      [Translations.get("SALAXY.NG1.CompanyImport.help.overview.title")],
      [Translations.get("SALAXY.NG1.CompanyImport.help.overview.row1")],
      [Translations.get("SALAXY.NG1.CompanyImport.help.overview.row2")],
      [""],
       ...this.getDefaultHelpDataForColumns(),
    ];
    await this.download(helpData);
  }

  protected async getImportMapper() {
    const mapper = new DataMapper<CompanyImportRow, CompanyImport>("NG1/CompanyImportController", "Asiakkaat");
    const map = {
      officialId: "item.officialId",
      partnerAccountId: "item.partnerAccountId",
      email: "item.email",
      telephone: "item.telephone",
      contactFirstName: "item.contactFirstName",
      contactLastname: "item.contactLastName",
      proxyPartner: "item.proxyPartner",
      language: "item.language",
      eInvoiceReceiver: "parameters.settings.payments.invoice.eInvoiceReceiver",
      eInvoiceIntermediator: "parameters.settings.payments.invoice.eInvoiceIntermediator",
    };
    await mapper.setSchemaFromCache(this.jsonSchemaService.getCache(), "salaxy-secure/schemas/v02.json", "CompanyImport", "target");
    mapper.setPaths(map);
    mapper.generateSchema("CompanyImportRow", "source");
    return mapper;
  }

  private async enrichEInvoiceAddresses() {
    if (!this.sourceData || this.sourceData.length == 0) {
      return;
    }
    const dialog = this.uiHelpers.showLoading("SALAXY.NG1.CompanyImport.enrichEInvoiceAddressesMessage", "");
    for (const row of this.sourceData) {
      if (row.eInvoiceReceiver && row.eInvoiceIntermediator) {
        continue;
      }
      dialog.setText(row.officialId);
      const addresses = await this.importsApi.queryEInvoiceAddress(row.officialId);
      if (addresses.length == 0) {
        continue;
      }
      if (addresses.length ==  1) {
        row.eInvoiceReceiver = addresses[0].receiver;
        row.eInvoiceIntermediator = addresses[0].intermediator;
        await this.validate();
        continue;
      }
      const currentSelection: EInvoiceAddress = null;
      const selectionDialog = await this.uiHelpers.openEditDialog("salaxy-components/helpers/import/EInvoiceAddressSelect.html", { selection: currentSelection }, { addresses, select: (current, row, close) => { current.selection = row; close(EditDialogKnownActions.Ok); } });
      if (selectionDialog.result == EditDialogKnownActions.Ok && selectionDialog.item.selection?.receiver && selectionDialog.item.selection?.intermediator) {
          row.eInvoiceReceiver = selectionDialog.item.selection?.receiver;
          row.eInvoiceIntermediator = selectionDialog.item.selection?.intermediator;
          await this.validate();
          continue;
      }
    }
    dialog.dismiss();
  }
}